import { API_CONFIG, API_ENDPOINTS, getHeadersWithOrg, getMultipartHeaders, apiCall, authFetch, getAuthToken, OAUTH_CONFIG, NotFoundError } from './client';
import { ErrorResponse } from '../../types';
import i18n from '../../i18n';
import {
  ConversionUploadResponse,
  ConversionStatusResponse as ConvStatusResponse,
  ConversionJobListResponse,
  HeadingsResponse,
  ChunkResponse as ConvChunkResponse,
  ReviewedTopic,
  StepConfig,
  WsTopicAnalysis,
  WsBatchQuestion,
} from '../../types/conversion';

// =============================================================================
// Document-to-DITA Conversion API
// =============================================================================

/**
 * Upload a PDF or Markdown file to start the conversion pipeline.
 * Returns a job_id for subsequent polling and operations.
 */
export async function uploadConversionPdf(
  siteId: string,
  orgId: string,
  file: File,
  stepConfig?: Partial<StepConfig>,
  basePath?: string,
): Promise<ConversionUploadResponse> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_UPLOAD(siteId)}`;
  const formData = new FormData();
  formData.append('file', file);
  // Only send step_config when at least one gate is being overridden; an
  // omitted field defaults to `true` (today's behavior) on the backend.
  if (stepConfig && Object.keys(stepConfig).length > 0) {
    formData.append('step_config', JSON.stringify(stepConfig));
  }
  // Base path (base href) prefixed onto asset_guids at the automated indexing
  // step. Omitted -> backend default; only sent when explicitly set.
  if (basePath && basePath.trim()) {
    formData.append('base_path', basePath.trim());
  }

  return apiCall<ConversionUploadResponse>(url, {
    method: 'POST',
    headers: getMultipartHeaders(orgId),
    body: formData,
  });
}

/**
 * List all users' conversion jobs for an authorized site (all statuses, newest
 * first, paginated). Replaces the deprecated singular status endpoint.
 */
export async function getConversionJobs(
  siteId: string,
  orgId: string,
  options?: { limit?: number; offset?: number },
): Promise<ConversionJobListResponse> {
  const params = new URLSearchParams();
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.offset !== undefined) params.set('offset', String(options.offset));
  const qs = params.toString();
  const base = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_JOBS(siteId)}`;
  return apiCall<ConversionJobListResponse>(`${base}${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });
}

/** Answer a pending clarifying question (topic Q&A gate). */
export async function answerConversionQuestion(
  siteId: string,
  jobId: string,
  orgId: string,
  questionId: string,
  answer: string,
): Promise<void> {
  await apiCall<unknown>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_ANSWER(siteId, jobId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ question_id: questionId, answer }),
    },
  );
}

/** Analyzed topic list for the review gate (REST fallback when no live socket). */
export async function getConversionTopics(
  siteId: string,
  jobId: string,
  orgId: string,
): Promise<{ job_id: string; topics: WsTopicAnalysis[] }> {
  return apiCall<{ job_id: string; topics: WsTopicAnalysis[] }>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_TOPICS(siteId, jobId)}`,
    { method: 'GET', headers: getHeadersWithOrg() },
  );
}

/** DITA topic types the generator supports, for the review type dropdown. */
export async function getConversionDitaTypes(
  siteId: string,
): Promise<{ types: { value: string; label: string }[] }> {
  return apiCall<{ types: { value: string; label: string }[] }>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_DITA_TYPES(siteId)}`,
    { method: 'GET', headers: getHeadersWithOrg() },
  );
}

/** Full markdown body of one topic, fetched lazily when a preview is opened
 *  (the topic/chunk lists and Q&A questions carry only `has_content`). */
export async function getConversionTopicContent(
  siteId: string,
  jobId: string,
  topicId: string,
): Promise<{ job_id: string; topic_id: string; content: string }> {
  return apiCall<{ job_id: string; topic_id: string; content: string }>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_TOPIC_CONTENT(siteId, jobId, topicId)}`,
    { method: 'GET', headers: getHeadersWithOrg() },
  );
}

/** Pending Q&A questions for the topic-analysis gate (REST fallback). */
export async function getConversionQuestions(
  siteId: string,
  jobId: string,
  orgId: string,
): Promise<{ job_id: string; pending_question_id?: string | null; questions: WsBatchQuestion[] }> {
  return apiCall<{ job_id: string; pending_question_id?: string | null; questions: WsBatchQuestion[] }>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_QUESTIONS(siteId, jobId)}`,
    { method: 'GET', headers: getHeadersWithOrg() },
  );
}

/** Resolve the job's referenced images (relative `media/<name>` paths) to
 *  short-lived, same-origin URLs the preview's <img> tags can load. */
export async function getConversionMediaUrls(
  siteId: string,
  jobId: string,
  orgId: string,
): Promise<{ job_id: string; urls: Record<string, string> }> {
  return apiCall<{ job_id: string; urls: Record<string, string> }>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_MEDIA_URLS(siteId, jobId)}`,
    { method: 'GET', headers: getHeadersWithOrg() },
  );
}

/** Let the AI resolve all remaining clarifying questions (topic Q&A gate). */
export async function resolveConversionQuestions(
  siteId: string,
  jobId: string,
  orgId: string,
): Promise<void> {
  await apiCall<unknown>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_RESOLVE_QUESTIONS(siteId, jobId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
    },
  );
}

/** Submit topic-list review decisions (topic review gate). */
export async function reviewConversionTopics(
  siteId: string,
  jobId: string,
  orgId: string,
  topics: ReviewedTopic[],
): Promise<void> {
  await apiCall<unknown>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_REVIEW(siteId, jobId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ topics }),
    },
  );
}

/** Confirm or reject the built package (package-confirm gate). */
export async function confirmConversionPackage(
  siteId: string,
  jobId: string,
  orgId: string,
  confirmed: boolean,
): Promise<void> {
  await apiCall<unknown>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_CONFIRM(siteId, jobId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ confirmed }),
    },
  );
}

/** Cancel a conversion job (no live socket required). */
export async function cancelConversion(
  siteId: string,
  jobId: string,
  orgId: string,
): Promise<void> {
  await apiCall<unknown>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_CANCEL(siteId, jobId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Poll the conversion job status.
 * Returns the current step, structgen status, and other metadata.
 */
export async function getConversionStatus(
  siteId: string,
  orgId: string,
  jobId?: string,
): Promise<ConvStatusResponse> {
  const base = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_STATUS(siteId)}`;
  const url = jobId ? `${base}?job_id=${encodeURIComponent(jobId)}` : base;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeadersWithOrg(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        // No active or recent job found - not an error for resume logic
        throw new NotFoundError('No active conversion job found');
      }
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to get conversion status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error(i18n.t('errors.conversion.statusFetchFailed'));
  }
}

/**
 * Fetch heading analysis for the converted markdown.
 * Only available after the job reaches 'markdown_ready' step.
 */
export async function getConversionHeadings(
  siteId: string,
  jobId: string,
  orgId: string,
): Promise<HeadingsResponse> {
  return apiCall<HeadingsResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_HEADINGS(siteId, jobId)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Chunk the markdown at a chosen heading depth.
 * Pass `null` for `depth` when the document has no headings — the whole
 * document is treated as a single DITA topic.
 * Returns topic previews for user review before WebSocket phase.
 */
export async function chunkConversion(
  siteId: string,
  jobId: string,
  orgId: string,
  depth: number | null,
): Promise<ConvChunkResponse> {
  return apiCall<ConvChunkResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_CHUNK(siteId, jobId)}`,
    {
      method: 'POST',
      headers: getHeadersWithOrg(),
      body: JSON.stringify({ depth }),
    },
  );
}

/**
 * Upload a text file of URLs to start the HTML-to-DITA conversion pipeline.
 * Each URL is crawled, cleaned, and converted to DITA automatically.
 */
export async function uploadHtmlUrls(
  siteId: string,
  orgId: string,
  file: File,
  options?: {
    baseUrl?: string;
    topicType?: string;
    urlAllowlist?: string[];
    crawlDepth?: number;
  },
): Promise<ConversionUploadResponse> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_HTML_URLS(siteId)}`;
  const formData = new FormData();
  formData.append('file', file);

  if (options?.baseUrl) {
    formData.append('base_url', options.baseUrl);
  }
  if (options?.topicType) {
    formData.append('topic_type', options.topicType);
  }
  if (options?.urlAllowlist && options.urlAllowlist.length > 0) {
    formData.append('url_allowlist', JSON.stringify(options.urlAllowlist));
  }
  if (options?.crawlDepth !== undefined) {
    formData.append('crawl_depth', String(options.crawlDepth));
  }

  return apiCall<ConversionUploadResponse>(url, {
    method: 'POST',
    headers: getMultipartHeaders(orgId),
    body: formData,
  });
}

/**
 * Build the WebSocket URL for the conversion agentic workflow.
 * Includes auth token and client_id as query parameters.
 */
export function buildConversionWsUrl(siteId: string, jobId: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const baseUrl = API_CONFIG.BASE_URL || window.location.origin;
  // Strip protocol from baseUrl to rebuild as ws
  const hostPath = baseUrl.replace(/^https?:\/\//, '').replace(/^wss?:\/\//, '');
  const wsPath = API_ENDPOINTS.CONVERSION_WS(siteId, jobId);

  const token = getAuthToken() || '';
  const clientId = OAUTH_CONFIG.CLIENT_ID || '';

  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (clientId) params.set('client_id', clientId);

  return `${protocol}//${hostPath}${wsPath}?${params.toString()}`;
}

/**
 * Build the download URL for the final DITA ZIP package (e.g. for display only).
 * Prefer `downloadConversionPackage` for user-triggered downloads so auth headers are sent.
 */
export function getConversionDownloadUrl(siteId: string, jobId: string): string {
  return `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_DOWNLOAD(siteId, jobId)}`;
}

/**
 * Download the final DITA ZIP with Bearer auth. A plain link cannot send Authorization,
 * so this uses fetch + blob + object URL.
 */
export async function downloadConversionPackage(
  siteId: string,
  _orgId: string,
  jobId: string,
  filename = 'dita-package.zip',
): Promise<void> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.CONVERSION_DOWNLOAD(siteId, jobId)}`;
  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData: ErrorResponse = await response.json().catch(() => ({
      error: `HTTP ${response.status}`,
    }));
    throw new Error(errorData.detail || errorData.error || `Download failed: ${response.status}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
